import { a3, PropsFromClass, registry } from "@lukekaalim/act-three";
import { Component, h, ReadOnlyRef, Ref, useEffect, useRef } from "@lukekaalim/act";
import * as three from "three";

import StarFieldShader from './StarField.vert.glsl?raw';

import { useBeat, useExternalRef, useIsDebug } from "@kosmod/kit";

import starryDiamondURL from '../../assets/starry_diamond.png';
import { useDisposable } from "../../hooks/useDisposable";
import { renderBeat } from "../../core";

export class StarFieldMaterial extends three.ShaderMaterial {
  vertexShader = StarFieldShader;
  fragmentShader = three.ShaderChunk.points_frag;

  map: three.Texture | null = null;// = new three.TextureLoader().load(starryDiamondURL) as three.Texture;

  vertexColors = true;
  transparent = true;
  alphaTest = 0.1;
  depthWrite = false;
  depthTest = true;

  constructor() {
    super()
  }

  get origin() {
    return this.uniforms.spawn_origin.value;
  }

  uniforms = {
    ...three.ShaderLib.points.uniforms,
    map: { value: this.map },
    now: { value: 0 },
    size: { value: 5 },
    spawn_origin: { value: new three.Vector3(0, 0, 0) },
    volume: { value: 20 },
    sizeAttenuation: { value: true },
    height: { value: 0 },
    opacity: { value: 1 },
    flicker_speed: { value: 1 },
    falloff: { value: 0 },
    diffuse: { value: new three.Color(`rgb(207, 228, 231)`) },
    wave_height: new three.Uniform<number>(1),
    wave_speed: new three.Uniform<number>(1),
  }
  defines = {
    'USE_COLOR_ALPHA': 1
  }
}
const SIZE = 50;

export type StarFieldProps = {
  ref?: Ref<StarField | null>,

  position?: three.Vector3Like,
  texture?: three.Texture,
  color?: three.Color,
  opacity?: number,
  size?: number,

  count?: number,
  height?: number,
  volume?: number,
  flickerSpeed?: number,

  waveHeight?: number,
  waveSpeed?: number,
}

export type StarField = three.Points<
  three.BufferGeometry<{ position: three.BufferAttribute, flicker_offset: three.BufferAttribute }>,
  StarFieldMaterial
>;

export const StarField: Component<StarFieldProps> = ({
  ref,
  position,
  color,
  size = 5,
  opacity = 1,
  count = 1024 * 16,
  height = 0,
  volume = 10,
  flickerSpeed = 1,
  texture = null,// = new three.TextureLoader().load(starryDiamondURL),

  waveHeight = 0.1,
  waveSpeed = 0.5,
}) => {
  const innerRef = useExternalRef(ref, null);

  const geometry = useDisposable(() => {
    const geo = new three.BufferGeometry();

    const positions = new Float32Array(Array.from({ length: count * 3 }).map((_, i) => {
      return Math.random()
    }));
    const flicker_offsets = new Float32Array(Array.from({ length: count }).map((_, i) => {
      return Math.random() * Math.PI
    }));

    geo.setAttribute('position', new three.BufferAttribute(positions, 3))
    geo.setAttribute('flicker_offset', new three.BufferAttribute(flicker_offsets, 1))
    return geo;
  }, [count]);

  const material = useDisposable(() => {
    const material = new StarFieldMaterial();

    if (color)
      material.uniforms.diffuse.value.copy(color)

    material.uniforms.height.value = height;
    material.uniforms.volume.value = volume;
    material.uniforms.size.value = size;
    material.uniforms.opacity.value = opacity;
    material.uniforms.flicker_speed.value = flickerSpeed;

    material.uniforms.wave_height.value = waveHeight;
    material.uniforms.wave_speed.value = waveSpeed;
    material.uniforms.map.value = texture;
    material.map = texture;

    return material;
  }, [color, height, volume, size, opacity, flickerSpeed, waveHeight, waveSpeed, texture])

  useBeat(renderBeat, now => {
    material.uniforms.now.value = now;
    //material.uniforms.spawn_origin.value.x += 0.001;
  });

  // useBeat(renderBeat, (now) => {
  //   ref.current?.position.set(system.camera_focus.x, -5, system.camera_focus.y);
    
  //   (ref.current.material as ParticleMaterial).uniforms.spawn_origin.value.set(system.camera_focus.x/4 + (now / 10000), -5, system.camera_focus.y/4);
  //   ref.current.material.uniforms.now.value = now;
  // })

  const isDebug = useIsDebug();
  

  return [
    h(a3.points, {
      ref: innerRef,
      geometry,
      material,
      position,
      frustumCulled: false,
    }, [
      isDebug && h(a3.Box3Helper, {
        box: new three.Box3(new three.Vector3(-volume/2, 0, -volume/2), new three.Vector3(volume/2, height, volume/2))
      })
    ]),
  ]
};

declare module "@lukekaalim/act-three" {
  interface ExtendedPrimitives {
    Box3Helper: PropsFromClass<three.Box3Helper> & { box: three.Box3, color?: three.Color }
  }
}

registry.registerPrimitive('Box3Helper', ({ box, color }) => new three.Box3Helper(box, color), () => {});